import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { store } from 'store';

export const apiInstance = axios.create({
  withCredentials: true,
  headers: { 'ngrok-skip-browser-warning': true },
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_API_VERSION}`
});

export const apiBaseInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_API_VERSION}`
});

export const apiAADInstance = axios.create({
  withCredentials: true,
  headers: { 'ngrok-skip-browser-warning': true },
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_API_VERSION}`
});

// Flag to track if we're already refreshing token
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const logout = async () => {
  // Only logout after multiple failed attempts or critical errors
  console.warn('Logging out due to authentication failure');
  window.location.href = `${process.env.REACT_APP_BASE_URL}/logout`;
};

apiInstance.interceptors.request.use(
  (config) => {
    const { getState } = store;
    const { auth } = getState();
    const { user } = auth;
    const { authData } = user;

    if (authData.token && authData.token !== '') {
      config.headers.Authorization = `Bearer ${authData.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest: any = error.config;
    const { response } = error;

    // If no response (network error), reject immediately
    if (!response) {
      return Promise.reject(error);
    }

    // Only handle 401 errors
    if (response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // Try to get MSAL instance and refresh token
      try {
        // Dynamically import to avoid circular dependencies
        const msalInstance = (window as any).__MSAL_INSTANCE__;

        if (msalInstance) {
          const accounts = msalInstance.getAllAccounts();

          if (accounts.length > 0) {
            const { loginRequest } = await import('config');
            const request = {
              ...loginRequest,
              account: accounts[0],
              forceRefresh: true
            };

            const response = await msalInstance.acquireTokenSilent(request);

            if (response.accessToken) {
              // Update token in store
              const { getToken } = await import('store/Auth');
              store.dispatch(getToken({ token: response.accessToken }));

              processQueue(null, response.accessToken);
              isRefreshing = false;

              // Retry original request with new token
              originalRequest.headers['Authorization'] = 'Bearer ' + response.accessToken;
              return axios(originalRequest);
            }
          }
        }

        // If we reach here, token refresh failed
        processQueue(error, null);
        isRefreshing = false;
        logout();
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        processQueue(refreshError, null);
        isRefreshing = false;
        logout();
      }
    }

    return Promise.reject(error);
  }
);

apiAADInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const { response } = error;

    // For AAD instance, allow the error to propagate without auto-logout
    // since this is used for initial authentication
    if (!response) {
      return Promise.reject(error);
    }

    if (response.status === 401) {
      console.warn('AAD authentication error - may need to re-authenticate');
    }

    return Promise.reject(error);
  }
);

apiBaseInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const { response } = error;

    if (!response) {
      return Promise.reject(error);
    }

    // Base instance should not trigger logout
    // Let the calling code handle 401 errors
    return Promise.reject(error);
  }
);

const responseData = <T>(response: AxiosResponse<T>) => response;

export const request = {
  aadApiGet: <T>(url: string, config?: AxiosRequestConfig) =>
    apiAADInstance.get<T>(url, config).then(responseData),
  baseGet: <T>(url: string, config?: AxiosRequestConfig) =>
    apiBaseInstance.get<T>(url, config).then(responseData),
  basePost: <T>(url: string, body: {}, config?: AxiosRequestConfig) =>
    apiBaseInstance.post<T>(url, body, config).then(responseData),
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiInstance.get<T>(url, config).then(responseData),
  post: <T>(url: string, body: {}, config?: AxiosRequestConfig) =>
    apiInstance.post<T>(url, body, config).then(responseData),
  put: <T>(url: string, body: {}, config?: AxiosRequestConfig) =>
    apiInstance.put<T>(url, body, config).then(responseData),
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiInstance.delete<T>(url, config).then(responseData)
};
